import React, { useState } from 'react';
import { Wallet, ShieldCheck, Lock, Loader2 } from 'lucide-react';
import { useKeycloak } from '../../auth/keycloakProvider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';

interface WalletSetupProps {
  onComplete: () => void;
}

export function WalletSetup({ onComplete }: WalletSetupProps) {
  const { setupWallet } = useKeycloak();
  const { toast } = useToast();
  const [walletPassword, setWalletPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (walletPassword.length < 8) {
      setError('A password deve ter pelo menos 8 caracteres');
      return;
    }

    if (walletPassword !== confirmPassword) {
      setError('As passwords não coincidem');
      setConfirmPassword('');
      return;
    }

    setIsLoading(true);
    try {
      await setupWallet(walletPassword);
      toast({
        title: 'Wallet configurada com sucesso',
        description: 'A sua wallet foi configurada e está pronta para uso.',
      });
      onComplete();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao configurar wallet';
      setError(errorMessage);
      toast({
        title: 'Erro ao configurar wallet',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <div className="w-full max-w-md animate-scale-in">
        <Card className="shadow-elevated">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <Wallet className="h-8 w-8 text-primary" />
              </div>
            </div>
            <CardTitle>Configurar Wallet</CardTitle>
            <CardDescription>
              Configure a sua wallet para poder assinar documentos eCMR de forma segura
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="walletPassword">Password da Wallet *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="walletPassword"
                    type="password"
                    value={walletPassword}
                    onChange={(e) => setWalletPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    className="pl-10"
                    required
                    minLength={8}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Esta password será usada para encriptar a sua wallet. Guarde-a em segurança.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirme a password"
                    className="pl-10"
                    required
                    minLength={8}
                  />
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
                <ShieldCheck className="h-5 w-5 text-primary mt-0.5" />
                <div className="flex-1 space-y-1 text-sm">
                  <p className="font-medium">Importante:</p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    <li>Esta password é necessária para aceder à sua wallet</li>
                    <li>Sem ela, não poderá assinar documentos eCMR</li>
                    <li>Guarde-a num local seguro</li>
                  </ul>
                </div>
              </div>

              <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    A configurar...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Configurar Wallet
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

